import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faFilter,
  faDownload,
  faPlay,
  faUndo,
  faThumbsDown,
  faLock,
  faServer,
  faCloud,
  faChartLine,
  faCog,
  faSync,
  faWarning,
  faSpinner,
  faSearch,
  faHistory,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { faGithub, faAws, faGoogle } from '@fortawesome/free-brands-svg-icons';

interface SecurityTabProps {
  loading?: boolean;
  externalScanId?: string | null;
  openHistoryPanel?: boolean;
  onHistoryPanelOpened?: () => void;
  /** AWS account id to pre-select in the account filter — set only when a chat query named
   *  one account. One-shot: cleared via onAccountFilterApplied, so opening this tab manually
   *  never carries a filter and keeps showing every account. */
  externalAccountFilter?: string | null;
  onAccountFilterApplied?: () => void;
}

interface ScanResult {
  success: boolean;
  scanId?: string;
  summary: {
    totalVulnerabilities: number;
    severitySplit: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    accountsScanned: number;
    regionsScanned: number;
    scanDuration: number;
  };
  vulnerabilities: any[];
  scanTimestamp: string;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'text-red-600 bg-red-100';
    case 'high': return 'text-orange-600 bg-orange-100';
    case 'medium': return 'text-yellow-600 bg-yellow-100';
    case 'low': return 'text-blue-600 bg-blue-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'open': return 'text-red-600 bg-red-100';
    case 'fix_available': return 'text-blue-600 bg-blue-100';
    case 'fix_deployed': return 'text-green-600 bg-green-100';
    case 'rolled_back': return 'text-orange-600 bg-orange-100';
    case 'ignored': return 'text-gray-600 bg-gray-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getCloudIcon = (provider: string) => {
  switch (provider) {
    case 'aws': return faAws;
    case 'gcp': return faGoogle;
    case 'github': return faGithub;
    default: return faCloud;
  }
};

export const SecurityTab: React.FC<SecurityTabProps> = ({ loading: propLoading = false, externalScanId = null, openHistoryPanel = false, onHistoryPanelOpened, externalAccountFilter = null, onAccountFilterApplied }) => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'vulnerabilities' | 'compliance' | 'recommendations'>('vulnerabilities');
  const [showFilters, setShowFilters] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [hasScanned, setHasScanned] = useState(false);
  const [selectedVulnerability, setSelectedVulnerability] = useState<any | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingAIFix, setLoadingAIFix] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [aiFixes, setAiFixes] = useState<{[key: string]: any}>({});
  const [dismissedVulnerabilities, setDismissedVulnerabilities] = useState<Set<string>>(new Set());
  const [selectedFixOption, setSelectedFixOption] = useState<string>('');
  const [customCidrInput, setCustomCidrInput] = useState<string>('');
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<string>('all');
  const [showHistory, setShowHistory] = useState(false);
  const [scanHistory, setScanHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);
  const [pendingDeployData, setPendingDeployData] = useState<{ vulnerability: any; fix: any } | null>(null);

  // Mock data for compliance and recommendations (will be replaced with real data later)
  const mockData = {
    compliance: [
      {
        name: 'SOC 2 Type II',
        overallScore: 85,
        requirements: [
          {
            id: 'cc6.1',
            title: 'Logical and Physical Access Controls',
            status: 'compliant',
            linkedVulnerabilities: [],
            recommendedActions: ['Review access logs quarterly', 'Update access control policies']
          },
          {
            id: 'cc6.2',
            title: 'Data Classification',
            status: 'partial',
            linkedVulnerabilities: ['vuln-001'],
            recommendedActions: ['Implement data classification scheme', 'Encrypt sensitive data at rest']
          }
        ]
      }
    ],
    recommendations: [
      {
        id: 'rec-001',
        title: 'Enable AWS GuardDuty',
        context: 'Threat detection service is not enabled in 3 accounts',
        risk: 'high',
        complianceLink: ['SOC 2 CC7.1'],
        estimatedEffort: '2-4 hours',
        suggestedFix: 'Enable GuardDuty in all AWS accounts to detect malicious activity'
      }
    ]
  };

  // Perform security scan
  const handleScan = async () => {
    try {
      setScanning(true);
      setScanProgress('Initializing security scan...');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      console.log('🔒 Security Tab: Starting scan...');

      setScanProgress('Scanning EC2 Security Groups...');

      const response = await fetch(`${baseUrl}/api/security/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ force: true })
      });

      if (!response.ok) {
        throw new Error(`Scan failed: ${response.statusText}`);
      }

      const result: ScanResult = await response.json();

      console.log('✅ Security Tab: Scan completed', result);

      setScanResult(result);
      setCurrentScanId(result.scanId || null);
      setHasScanned(true);
      setScanProgress('Scan completed!');

      setTimeout(() => {
        setScanProgress('');
      }, 2000);

    } catch (error: any) {
      console.error('❌ Security Tab: Scan error:', error);
      setScanProgress(`Error: ${error.message}`);
      setTimeout(() => {
        setScanProgress('');
      }, 5000);
    } finally {
      setScanning(false);
    }
  };

  const handleRefresh = () => {
    handleScan();
  };

  // Export vulnerabilities to CSV
  const handleExportCSV = () => {
    if (!scanResult || currentVulnerabilities.length === 0) {
      alert('No vulnerabilities to export');
      return;
    }

    const csvHeaders = ['Account', 'Account ID', 'Region', 'Service', 'Resource', 'Issue', 'Severity', 'Status', 'Recommended Fix', 'Compliance'];
    const csvRows = currentVulnerabilities.map(v => [
      v.accountName,
      v.accountId,
      v.region,
      v.service,
      v.resource,
      v.issueDescription,
      v.severity,
      v.status,
      v.recommendedFix,
      v.complianceMapping?.join('; ') || 'N/A'
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-scan-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get AI fix recommendation
  const handleGetAIFix = async (vulnerability: any) => {
    try {
      setLoadingAIFix(vulnerability.id);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/security/fix-recommendation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ vulnerability, scanId: currentScanId })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI fix recommendation');
      }

      const result = await response.json();
      setAiFixes(prev => ({
        ...prev,
        [vulnerability.id]: result
      }));

      // Auto-select the default option if available
      if (result.options && result.options.length > 0) {
        const defaultOption = result.options.find((opt: any) => opt.isDefault);
        if (defaultOption) {
          setSelectedFixOption(defaultOption.id);
        } else {
          setSelectedFixOption(result.options[0].id);
        }
      }

    } catch (error: any) {
      console.error('❌ Security Tab: AI Fix error:', error);
      alert(`Failed to get AI fix: ${error.message}`);
    } finally {
      setLoadingAIFix(null);
    }
  };

  // Apply AI fix
  const handleApplyFix = async (vulnerability: any, fix: any) => {
    // Check if option selection is required
    if (!selectedFixOption && fix.options && fix.options.length > 0) {
      alert('Please select a fix option');
      return;
    }

    // Find the selected option details
    const selectedOption = fix.options?.find((opt: any) => opt.id === selectedFixOption);

    // Check if custom input is required
    if (selectedOption?.requiresInput && !customCidrInput.trim()) {
      const userInput = window.prompt('Please enter custom CIDR blocks (comma-separated):\nExample: 10.0.0.0/16, 192.168.1.0/24');
      if (!userInput || !userInput.trim()) {
        alert('Custom CIDR input is required for this option');
        return;
      }
      setCustomCidrInput(userInput);
    }

    // Show custom deploy confirmation dialog
    setPendingDeployData({ vulnerability, fix });
    setShowDeployConfirm(true);
  };

  // Dismiss vulnerability
  const handleDismissVulnerability = async (vulnerability: any) => {
    if (!window.confirm('Are you sure you want to dismiss this vulnerability? It will be marked as closed.')) {
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/security/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          vulnerabilityId: vulnerability.id,
          reason: 'User dismissed from UI',
          scanId: currentScanId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss vulnerability');
      }

      // Add to dismissed set
      setDismissedVulnerabilities(prev => {
        const newSet = new Set(prev);
        newSet.add(vulnerability.id);
        return newSet;
      });
      setShowDetailsModal(false);
      alert('Vulnerability dismissed successfully');

    } catch (error: any) {
      console.error('❌ Security Tab: Dismiss error:', error);
      alert(`Failed to dismiss vulnerability: ${error.message}`);
    }
  };

  // View vulnerability details
  const handleViewDetails = (vulnerability: any) => {
    setSelectedVulnerability(vulnerability);
    setShowDetailsModal(true);
    // Reset fix selection state when opening a new vulnerability
    setSelectedFixOption('');
    setCustomCidrInput('');
  };

  const loadHistory = async () => {
    if (historyLoading) return;
    setHistoryLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${baseUrl}/api/security/scan/history`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
      });
      if (response.ok) {
        const data = await response.json();
        setScanHistory(data.scans || []);
      }
    } catch (err) {
      console.error('❌ Security Tab: Failed to load scan history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadScanFromHistory = async (scanId: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${baseUrl}/api/security/scan/${scanId}`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setScanResult(result as ScanResult);
          setCurrentScanId(scanId);
          setHasScanned(true);
          setShowHistory(false);
        }
      }
    } catch (err) {
      console.error('❌ Security Tab: Failed to load historical scan:', err);
    }
  };

  // Load cached scan results on mount
  // Load a specific scan when externalScanId is set (triggered from chat)
  useEffect(() => {
    if (!externalScanId) return;

    const loadScanById = async () => {
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
        const token = localStorage.getItem('auth_token');

        const response = await fetch(`${baseUrl}/api/security/scan/${externalScanId}`, {
          headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setScanResult(result as ScanResult);
            setCurrentScanId(externalScanId);
            setHasScanned(true);
            console.log('✅ Security Tab: Loaded scan from chat, scanId:', externalScanId);
          }
        }
      } catch (error) {
        console.error('❌ Security Tab: Failed to load scan by ID:', error);
      } finally {
        setLoading(false);
      }
    };

    loadScanById();
  }, [externalScanId]);

  useEffect(() => {
    console.log('SecurityTab mounted successfully');

    const loadLatestScan = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
        const token = localStorage.getItem('auth_token');

        // Use history endpoint — reads MongoDB only, never triggers a fresh AWS scan
        const response = await fetch(`${baseUrl}/api/security/scan/history`, {
          headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
        });

        if (response.ok) {
          const data = await response.json();
          const latest = data.scans?.[0]; // sorted desc — newest first
          if (latest?._id) {
            // Fetch full scan data for the latest entry
            const scanResponse = await fetch(`${baseUrl}/api/security/scan/${latest._id}`, {
              headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
            });
            if (scanResponse.ok) {
              const result = await scanResponse.json();
              if (result.success) {
                setScanResult(result as ScanResult);
                setCurrentScanId(latest._id);
                setHasScanned(true);
                console.log('✅ Security Tab: Loaded latest scan from history, scanId:', latest._id);
              }
            }
          }
        }
      } catch (error) {
        console.log('⚠️  Security Tab: No previous scan available');
      }
    };

    // Only load if no external scan is being loaded
    if (!externalScanId) loadLatestScan();
  }, []);

  // Open history panel when triggered from chat "Browse all" button
  useEffect(() => {
    if (!openHistoryPanel) return;
    setShowHistory(true);
    if (scanHistory.length === 0) loadHistory();
    onHistoryPanelOpened?.();
  }, [openHistoryPanel]);

  // Pre-select the account filter when a chat query asked about one specific account.
  // Cleared in the parent right after, so a later manual visit starts at 'all' as before.
  useEffect(() => {
    if (!externalAccountFilter) return;
    console.log('🔒 Security Tab: pre-selecting account filter from chat:', externalAccountFilter);
    setSelectedAccountFilter(externalAccountFilter);
    onAccountFilterApplied?.();
  }, [externalAccountFilter]);

  // Get current data (scan result or mock)
  const currentSummary = scanResult?.summary || {
    totalVulnerabilities: 0,
    severitySplit: { critical: 0, high: 0, medium: 0, low: 0 },
    accountsScanned: 0,
    regionsScanned: 0,
    scanDuration: 0
  };

  const currentVulnerabilities = scanResult?.vulnerabilities || [];

  // Get unique accounts for filter dropdown
  const uniqueAccounts = Array.from(
    new Map(currentVulnerabilities.map(v => [v.accountId, { id: v.accountId, name: v.accountName }])).values()
  );

  // Filter vulnerabilities by selected account
  const filteredVulnerabilities = selectedAccountFilter === 'all'
    ? currentVulnerabilities
    : currentVulnerabilities.filter(v => v.accountId === selectedAccountFilter);

  // Get per-account statuses from scan result
  const accountStatuses: any[] = (scanResult as any)?.accountStatuses || [];

  // Calculate vulnerability distribution by type (use filtered)
  const vulnerabilityByType = filteredVulnerabilities.reduce((acc: any, vuln: any) => {
    const type = vuln.service || 'Unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Calculate vulnerability distribution by issue category (use filtered)
  const vulnerabilityByCategory = filteredVulnerabilities.reduce((acc: any, vuln: any) => {
    let category = 'Other';
    if (vuln.issueDescription.includes('port')) category = 'Open Ports';
    else if (vuln.issueDescription.includes('public')) category = 'Public Access';
    else if (vuln.issueDescription.includes('encryption')) category = 'Missing Encryption';
    else if (vuln.issueDescription.includes('IAM') || vuln.issueDescription.includes('permission')) category = 'IAM Permissions';
    else if (vuln.issueDescription.includes('key')) category = 'Access Keys';

    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  if (loading || propLoading) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Scan Banner - Show if no scan has been performed */}
      {!hasScanned && !scanning && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex items-start space-x-2">
            <FontAwesomeIcon icon={faShieldAlt} className="text-blue-600 text-sm mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-blue-900 mb-0.5">Ready to Scan Your AWS Infrastructure</h3>
              <p className="text-xs text-blue-700 mb-2">
                Click "Start Security Scan" to analyze all AWS accounts and regions for security vulnerabilities including EC2 security groups, S3 policies, IAM permissions, and expired keys.
              </p>
              <button
                onClick={handleScan}
                className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                Start Security Scan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanning Progress Banner */}
      {scanning && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faSpinner} className="text-blue-600 text-sm animate-spin" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-blue-900 mb-0.5">Scanning In Progress...</h3>
              <p className="text-xs text-blue-700">
                {scanProgress || 'Scanning all AWS accounts and regions for security vulnerabilities...'}
              </p>
              <div className="mt-2 bg-blue-200 rounded-full h-1.5 overflow-hidden">
                <div className="bg-blue-600 h-full rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-base font-bold text-gray-900 mb-1">Security Center</h1>
            <p className="text-xs text-gray-600">
              Monitor, manage, and remediate security risks across all connected accounts
            </p>
            {scanResult && (
              <p className="text-xs text-gray-500 mt-0.5">
                Last scan: {new Date(scanResult.scanTimestamp).toLocaleString()} •
                {currentSummary.accountsScanned} accounts • {currentSummary.regionsScanned} regions •
                Duration: {currentSummary.scanDuration}s
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <FontAwesomeIcon icon={scanning ? faSpinner : faSearch} className={`mr-1 text-xs ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : 'Scan Now'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={scanning}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              <FontAwesomeIcon icon={faSync} className="mr-1 text-xs" />
              Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={!scanResult || currentVulnerabilities.length === 0}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-1 text-xs" />
              Export CSV
            </button>
            <button
              onClick={() => {
                const next = !showHistory;
                setShowHistory(next);
                if (next && scanHistory.length === 0) loadHistory();
              }}
              className={`px-3 py-1.5 text-sm border rounded font-medium flex items-center space-x-1 ${
                showHistory
                  ? 'bg-gray-100 border-gray-400 text-gray-800'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FontAwesomeIcon icon={faHistory} className="text-xs" />
              <span>History</span>
              <FontAwesomeIcon icon={showHistory ? faChevronUp : faChevronDown} className="text-xs" />
            </button>
          </div>
        </div>

        {/* Scan History dropdown */}
        {showHistory && (
          <div className="mt-2 border border-gray-200 rounded bg-gray-50">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-700">Previous Scans</span>
              {historyLoading && (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs text-gray-400" />
              )}
            </div>
            {!historyLoading && scanHistory.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No previous scans found.</p>
            )}
            {scanHistory.map((scan: any) => {
              const s = scan.summary?.severitySplit || {};
              const isActive = currentScanId === scan._id;
              return (
                <button
                  key={scan._id}
                  onClick={() => loadScanFromHistory(scan._id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left border-b border-gray-100 last:border-0 hover:bg-white transition-colors ${
                    isActive ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                    <span className="text-xs font-medium text-gray-700">
                      {new Date(scan.scanTimestamp).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-xs">
                    <span className="text-red-600">{s.critical ?? 0}C</span>
                    <span className="text-orange-500">{s.high ?? 0}H</span>
                    <span className="text-yellow-600">{s.medium ?? 0}M</span>
                    <span className="text-gray-400">{scan.summary?.totalVulnerabilities ?? 0} total</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-600">Total Vulnerabilities</div>
                <div className="text-xl font-bold text-gray-900">{currentSummary.totalVulnerabilities}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  <span className="text-red-600">{currentSummary.severitySplit.critical} Critical</span> •
                  <span className="text-orange-600 ml-1">{currentSummary.severitySplit.high} High</span>
                </div>
              </div>
              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-base" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-600">Accounts Scanned</div>
                <div className="text-xl font-bold text-gray-900">{currentSummary.accountsScanned}</div>
                <div className="text-xs text-gray-500 mt-0.5">AWS accounts analyzed</div>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                <FontAwesomeIcon icon={faServer} className="text-blue-600 text-base" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-600">Regions Scanned</div>
                <div className="text-xl font-bold text-gray-900">{currentSummary.regionsScanned}</div>
                <div className="text-xs text-gray-500 mt-0.5">Across all accounts</div>
              </div>
              <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                <FontAwesomeIcon icon={faCloud} className="text-purple-600 text-base" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-600">Scan Duration</div>
                <div className="text-xl font-bold text-gray-900">{currentSummary.scanDuration}s</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Last scan performance
                </div>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                <FontAwesomeIcon icon={faClock} className="text-green-600 text-base" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Posture Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        {/* Severity Distribution */}
        <div className="bg-white border border-gray-300 rounded p-3">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center justify-between">
            <span>By Severity</span>
            <span className="text-xs text-gray-500 font-normal">{currentSummary.totalVulnerabilities} total</span>
          </h3>
          <div className="space-y-1.5">
            {Object.entries(currentSummary.severitySplit).map(([severity, count]) => {
              const percentage = currentSummary.totalVulnerabilities > 0 ? ((count as number / currentSummary.totalVulnerabilities) * 100).toFixed(1) : '0';
              return (
                <div key={severity}>
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        severity === 'critical' ? 'bg-red-500' :
                        severity === 'high' ? 'bg-orange-500' :
                        severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}></div>
                      <span className="text-xs capitalize text-gray-700">{severity}</span>
                    </div>
                    <span className="text-xs font-medium text-gray-900">{`${count} (${percentage}%)`}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        severity === 'critical' ? 'bg-red-500' :
                        severity === 'high' ? 'bg-orange-500' :
                        severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Service Type */}
        <div className="bg-white border border-gray-300 rounded p-3">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center justify-between">
            <span>By Service</span>
            <span className="text-xs text-gray-500 font-normal">{Object.keys(vulnerabilityByType).length} services</span>
          </h3>
          <div className="space-y-1.5 max-h-24 overflow-y-auto">
            {Object.entries(vulnerabilityByType)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .slice(0, 5)
              .map(([service, count]) => {
                const percentage = currentSummary.totalVulnerabilities > 0 ? ((count as number / currentSummary.totalVulnerabilities) * 100).toFixed(1) : '0';
                return (
                  <div key={service}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-700">{service}</span>
                      <span className="text-xs font-medium text-gray-900">{`${count} (${percentage}%)`}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-purple-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* By Issue Category */}
        <div className="bg-white border border-gray-300 rounded p-3">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center justify-between">
            <span>By Category</span>
            <span className="text-xs text-gray-500 font-normal">{Object.keys(vulnerabilityByCategory).length} categories</span>
          </h3>
          <div className="space-y-1.5">
            {Object.entries(vulnerabilityByCategory)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(([category, count]) => {
                const percentage = currentSummary.totalVulnerabilities > 0 ? ((count as number / currentSummary.totalVulnerabilities) * 100).toFixed(1) : '0';
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-700">{category}</span>
                      <span className="text-xs font-medium text-gray-900">{`${count} (${percentage}%)`}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-indigo-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border border-gray-300 rounded">
        <div className="flex space-x-1 p-1">
          <button
            onClick={() => setActiveView('vulnerabilities')}
            className={`px-3 py-1.5 text-xs font-medium rounded ${
              activeView === 'vulnerabilities'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Vulnerabilities
          </button>
          {/* 
          <button
            onClick={() => setActiveView('compliance')}
            className={`px-3 py-1.5 text-xs font-medium rounded ${
              activeView === 'compliance'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
            Compliance
          </button>
          <button
            onClick={() => setActiveView('recommendations')}
            className={`px-3 py-1.5 text-xs font-medium rounded ${
              activeView === 'recommendations'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-1" />
            Recommendations
          </button>
          */}
        </div>
      </div>

      {/* Content based on active view */}
      {activeView === 'vulnerabilities' && (
        <div className="bg-white border border-gray-300 rounded">
          {/* Filters */}
          <div className="p-2 border-b border-gray-300">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Vulnerability Management</h3>
              <div className="flex items-center space-x-2">
                {/* Account Filter Dropdown */}
                {uniqueAccounts.length > 0 && (
                  <select
                    value={selectedAccountFilter}
                    onChange={(e) => setSelectedAccountFilter(e.target.value)}
                    className="px-2 py-1 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">All Accounts ({uniqueAccounts.length})</option>
                    {uniqueAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name} ({currentVulnerabilities.filter(v => v.accountId === acc.id).length} issues)
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-2 py-1 bg-white border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50 font-medium"
                >
                  <FontAwesomeIcon icon={faFilter} className="mr-1 text-xs" />
                  Filters
                </button>
              </div>
            </div>

            {/* Per-account scan status (show when filters expanded) */}
            {showFilters && accountStatuses.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-xs font-medium text-gray-600 mb-1">Account Scan Status:</div>
                {accountStatuses.map((acc: any) => (
                  <div key={acc.accountId} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700">{acc.accountName} ({acc.accountId})</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                      acc.status === 'success' ? 'text-green-700 bg-green-100' :
                      acc.status === 'skipped' ? 'text-yellow-700 bg-yellow-100' :
                      acc.status === 'error' ? 'text-red-700 bg-red-100' :
                      'text-gray-600 bg-gray-100'
                    }`}>
                      {acc.status === 'success' ? `✓ ${acc.vulnerabilities} issues` :
                       acc.status === 'skipped' ? `⚠ Skipped` :
                       acc.status === 'error' ? `✗ Error` : acc.status}
                    </span>
                  </div>
                ))}
                {accountStatuses.filter((a: any) => a.status === 'skipped' || a.status === 'error').map((acc: any) => (
                  acc.error && (
                    <div key={`err-${acc.accountId}`} className="text-[10px] text-orange-600 bg-orange-50 border border-orange-200 rounded p-1.5 mt-1">
                      ⚠ {acc.accountName}: {acc.error}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Vulnerability Table */}
          <div className="overflow-x-auto">
            {filteredVulnerabilities.length === 0 ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faShieldAlt} className="text-gray-300 text-3xl mb-2" />
                <div className="text-sm text-gray-500">
                  {!hasScanned ? 'Run a security scan to detect vulnerabilities' :
                   selectedAccountFilter !== 'all' ? 'No vulnerabilities found for this account.' :
                   'No vulnerabilities found! Your infrastructure looks secure.'}
                </div>
                {selectedAccountFilter !== 'all' && hasScanned && (
                  <button
                    onClick={() => setSelectedAccountFilter('all')}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    Show all accounts
                  </button>
                )}
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Account</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Region</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Service/Resource</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Issue</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Severity</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                    <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredVulnerabilities.filter(v => !dismissedVulnerabilities.has(v.id)).map((vulnerability) => (
                    <tr key={vulnerability.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center space-x-1.5">
                          <FontAwesomeIcon icon={getCloudIcon(vulnerability.cloudProvider)} className="text-gray-400 text-xs" />
                          <span className="text-xs font-medium text-gray-900">{vulnerability.accountName}</span>
                        </div>
                        <div className="text-[10px] text-gray-500">{vulnerability.accountId}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="text-xs text-gray-700">{vulnerability.region}</span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div>
                          <div className="text-xs font-medium text-gray-900">{vulnerability.service}</div>
                          <div className="text-[10px] text-gray-500 truncate max-w-[150px]" title={vulnerability.resource}>
                            {vulnerability.resource}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="text-xs text-gray-900 max-w-xs" title={vulnerability.issueDescription}>
                          {vulnerability.issueDescription}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{vulnerability.recommendedFix}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getSeverityColor(vulnerability.severity)}`}>
                          {vulnerability.severity}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(vulnerability.status)}`}>
                          {vulnerability.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => handleViewDetails(vulnerability)}
                            className="text-blue-600 hover:text-blue-800 text-[10px] text-left"
                            title="View full details"
                          >
                            <FontAwesomeIcon icon={faSearch} className="mr-1 text-[9px]" />
                            View Details
                          </button>
                          <button
                            onClick={() => handleGetAIFix(vulnerability)}
                            disabled={loadingAIFix === vulnerability.id}
                            className="text-purple-600 hover:text-purple-800 text-[10px] text-left disabled:opacity-50"
                            title="Get AI-powered fix recommendation"
                          >
                            <FontAwesomeIcon icon={loadingAIFix === vulnerability.id ? faSpinner : faCog} className={`mr-1 text-[9px] ${loadingAIFix === vulnerability.id ? 'animate-spin' : ''}`} />
                            {loadingAIFix === vulnerability.id ? 'Loading...' : 'Fix'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* 
      {activeView === 'compliance' && (
        <div className="space-y-2">
          {mockData.compliance.map((framework) => (
            <div key={framework.name} className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-900">{framework.name}</h3>
                <div className="flex items-center space-x-1.5">
                  <span className="text-xs text-gray-600">Overall Score:</span>
                  <span className={`text-sm font-bold ${
                    framework.overallScore >= 80 ? 'text-green-600' :
                    framework.overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {framework.overallScore}%
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                {framework.requirements.map((requirement) => (
                  <div key={requirement.id} className="border border-gray-300 rounded p-2">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-xs font-medium text-gray-900">{requirement.title}</h4>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        requirement.status === 'compliant' ? 'text-green-600 bg-green-100' :
                        requirement.status === 'partial' ? 'text-yellow-600 bg-yellow-100' :
                        'text-red-600 bg-red-100'
                      }`}>
                        {requirement.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {requirement.recommendedActions.map((action, index) => (
                        <div key={index} className="text-xs text-gray-600 flex items-start">
                          <span className="text-blue-500 mr-1.5">•</span>
                          {action}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      */}

      {/* 
      {activeView === 'recommendations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {mockData.recommendations.map((recommendation) => (
            <div key={recommendation.id} className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-900">{recommendation.title}</h3>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  recommendation.risk === 'high' ? 'text-red-600 bg-red-100' :
                  recommendation.risk === 'medium' ? 'text-yellow-600 bg-yellow-100' :
                  'text-blue-600 bg-blue-100'
                }`}>
                  {recommendation.risk} risk
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-2">{recommendation.context}</p>
              <div className="space-y-1 mb-2">
                <div className="text-xs">
                  <span className="font-medium text-gray-700">Compliance:</span> {recommendation.complianceLink.join(', ')}
                </div>
                <div className="text-xs">
                  <span className="font-medium text-gray-700">Estimated Effort:</span> {recommendation.estimatedEffort}
                </div>
              </div>
              <div className="text-xs text-gray-600 mb-2">{recommendation.suggestedFix}</div>
              <div className="flex space-x-1.5">
                <button className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors">
                  Apply Fix
                </button>
                <button className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      */}

      {/* View Details Modal */}
      {showDetailsModal && selectedVulnerability && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Vulnerability Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="text-xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Severity Badge */}
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(selectedVulnerability.severity)}`}>
                    {selectedVulnerability.severity.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedVulnerability.status)}`}>
                    {selectedVulnerability.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {/* Account & Resource Info */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Account</div>
                      <div className="text-sm text-gray-900">{selectedVulnerability.accountName}</div>
                      <div className="text-xs text-gray-500">{selectedVulnerability.accountId}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Region</div>
                      <div className="text-sm text-gray-900">{selectedVulnerability.region}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Service / Resource</div>
                    <div className="text-sm text-gray-900">{selectedVulnerability.service}</div>
                    <div className="text-xs text-gray-500 font-mono break-all">{selectedVulnerability.resource}</div>
                  </div>
                </div>

                {/* Issue Description */}
                <div>
                  <div className="text-xs font-semibold text-gray-900 mb-2">Issue Description</div>
                  <div className="text-sm text-gray-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    {selectedVulnerability.issueDescription}
                  </div>
                </div>

                {/* Recommended Fix */}
                <div>
                  <div className="text-xs font-semibold text-gray-900 mb-2">Recommended Fix</div>
                  <div className="text-sm text-gray-700 bg-green-50 border border-green-200 rounded-lg p-3">
                    {selectedVulnerability.recommendedFix}
                  </div>
                </div>

                {/* Compliance Mapping */}
                {selectedVulnerability.complianceMapping && selectedVulnerability.complianceMapping.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-900 mb-2">Compliance Frameworks</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedVulnerability.complianceMapping.map((framework: string) => (
                        <span key={framework} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                          {framework}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Fix Recommendation */}
                {aiFixes[selectedVulnerability.id] && (
                  <div>
                    <div className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faCog} className="mr-2 text-purple-600" />
                      AI-Powered Fix Recommendation
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
                      <div>
                        <div className="text-xs font-medium text-purple-900 mb-1">Analysis</div>
                        <div className="text-sm text-purple-800">{aiFixes[selectedVulnerability.id].analysis}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-purple-900 mb-1">Recommended Action</div>
                        <div className="text-sm text-purple-800 font-medium">{aiFixes[selectedVulnerability.id].action}</div>
                      </div>
                      {aiFixes[selectedVulnerability.id].commands && (
                        <div>
                          <div className="text-xs font-medium text-purple-900 mb-1">Commands</div>
                          <pre className="text-xs bg-purple-900 text-purple-100 p-2 rounded overflow-x-auto">
                            {aiFixes[selectedVulnerability.id].commands}
                          </pre>
                        </div>
                      )}

                      {/* Display metadata if available (e.g., IAM key info) */}
                      {aiFixes[selectedVulnerability.id].metadata && Object.keys(aiFixes[selectedVulnerability.id].metadata).length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                          <div className="text-xs font-medium text-blue-900 mb-1">Additional Information</div>
                          <div className="text-xs text-blue-800 space-y-1">
                            {aiFixes[selectedVulnerability.id].metadata.lastUsed && (
                              <div>Last Used: {aiFixes[selectedVulnerability.id].metadata.lastUsed}</div>
                            )}
                            {aiFixes[selectedVulnerability.id].metadata.keyAge !== null && (
                              <div>Key Age: {aiFixes[selectedVulnerability.id].metadata.keyAge} days</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fix Options (if available) */}
                      {aiFixes[selectedVulnerability.id].options && aiFixes[selectedVulnerability.id].options.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-blue-900 mb-2">📋 Choose Fix Strategy:</div>
                          <div className="space-y-2">
                            {aiFixes[selectedVulnerability.id].options.map((option: any) => (
                              <label
                                key={option.id}
                                className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                  selectedFixOption === option.id
                                    ? option.isDefault
                                      ? 'border-green-500 bg-green-50'
                                      : 'border-blue-500 bg-blue-50'
                                    : option.isDefault
                                      ? 'border-green-300 bg-green-50 hover:border-green-400'
                                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="fix-option"
                                  value={option.id}
                                  checked={selectedFixOption === option.id}
                                  onChange={(e) => setSelectedFixOption(e.target.value)}
                                  className="mt-1 mr-3"
                                />
                                <div className="flex-1">
                                  <div className={`text-sm font-semibold ${selectedFixOption === option.id ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {option.label}
                                    {option.isDefault && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Recommended</span>}
                                  </div>
                                  <div className={`text-xs mt-1 ${selectedFixOption === option.id ? 'text-blue-800' : 'text-gray-600'}`}>
                                    {option.description}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>

                          {/* Custom CIDR Input (if custom option is selected) */}
                          {selectedFixOption === 'custom_cidr' && (
                            <div className="mt-3 bg-white p-3 border border-blue-300 rounded-lg">
                              <label className="text-sm font-semibold text-blue-900 mb-2 block">
                                📝 Enter Custom CIDR Blocks:
                              </label>
                              <input
                                type="text"
                                value={customCidrInput}
                                onChange={(e) => setCustomCidrInput(e.target.value)}
                                placeholder="e.g., 10.0.0.0/16, 192.168.1.0/24"
                                className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="text-xs text-blue-700 mt-2">
                                💡 Enter one or more CIDR blocks separated by commas
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApplyFix(selectedVulnerability, aiFixes[selectedVulnerability.id])}
                          disabled={isDeploying}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                          <FontAwesomeIcon icon={isDeploying ? faSpinner : faPlay} className={`mr-1 ${isDeploying ? 'animate-spin' : ''}`} />
                          {isDeploying ? 'Deploying...' : 'Deploy Fix'}
                        </button>
                        <button
                          onClick={() => {
                            const newFixes = { ...aiFixes };
                            delete newFixes[selectedVulnerability.id];
                            setAiFixes(newFixes);
                          }}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Get AI Fix Button (if not already loaded) */}
                {!aiFixes[selectedVulnerability.id] && (
                  <button
                    onClick={() => handleGetAIFix(selectedVulnerability)}
                    disabled={loadingAIFix === selectedVulnerability.id}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={loadingAIFix === selectedVulnerability.id ? faSpinner : faCog} className={`mr-2 ${loadingAIFix === selectedVulnerability.id ? 'animate-spin' : ''}`} />
                    {loadingAIFix === selectedVulnerability.id ? 'Getting Fix...' : 'Fix'}
                  </button>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => handleDismissVulnerability(selectedVulnerability)}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors"
              >
                <FontAwesomeIcon icon={faThumbsDown} className="mr-2" />
                Dismiss Vulnerability
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Fix Confirmation Dialog */}
      {showDeployConfirm && pendingDeployData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden" style={{ animation: 'fadeInScale 0.2s ease-out' }}>
            {/* Dialog Header */}
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faPlay} className="text-purple-600 text-sm" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Deploy Fix</h3>
                  <p className="text-xs text-gray-500 mt-0.5">This action will modify your AWS resources</p>
                </div>
              </div>
            </div>

            {/* Dialog Body */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700 mb-3">Are you sure you want to deploy this fix?</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Service</span>
                  <span className="text-gray-900 font-medium">{pendingDeployData.vulnerability.service}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Region</span>
                  <span className="text-gray-900">{pendingDeployData.vulnerability.region}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Resource</span>
                  <span className="text-gray-900 text-right max-w-[200px] truncate" title={pendingDeployData.vulnerability.resource}>{pendingDeployData.vulnerability.resource}</span>
                </div>
                {selectedFixOption && pendingDeployData.fix.options && (() => {
                  const opt = pendingDeployData.fix.options.find((o: any) => o.id === selectedFixOption);
                  return opt ? (
                    <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
                      <span className="text-gray-500 font-medium">Fix Strategy</span>
                      <span className="text-purple-700 font-medium">{opt.label}</span>
                    </div>
                  ) : null;
                })()}
                {selectedFixOption === 'custom_cidr' && customCidrInput && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">CIDR Blocks</span>
                    <span className="text-gray-900 font-mono">{customCidrInput}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeployConfirm(false);
                  setPendingDeployData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                No
              </button>
              <button
                onClick={async () => {
                  setShowDeployConfirm(false);
                  const { vulnerability, fix } = pendingDeployData!;
                  setPendingDeployData(null);
                  // Proceed with deploy
                  try {
                    setIsDeploying(true);
                    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
                    const token = localStorage.getItem('auth_token');
                    const response = await fetch(`${baseUrl}/api/security/apply-fix`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token && { 'Authorization': `Bearer ${token}` })
                      },
                      body: JSON.stringify({
                        vulnerability,
                        fix,
                        selectedOption: selectedFixOption,
                        customInput: customCidrInput,
                        scanId: currentScanId
                      })
                    });
                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.message || errorData.error || 'Failed to deploy fix');
                    }
                    const result = await response.json();
                    alert(`✅ Fix deployed successfully!\n\n${result.message}`);
                    setSelectedFixOption('');
                    setCustomCidrInput('');
                    handleScan();
                    setShowDetailsModal(false);
                  } catch (error: any) {
                    console.error('❌ Security Tab: Deploy fix error:', error);
                    alert(`Failed to deploy fix:\n\n${error.message}`);
                  } finally {
                    setIsDeploying(false);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;